import { ChevronRight } from 'lucide-react';
import React from 'react';

import { cn } from '../../lib/utils';
import { Button } from '../ui/button';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface ActionItem {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  variant?:
    'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  disabled?: boolean;
}

export interface PageHeaderProps {
  title: string;
  description?: string;
  breadcrumbs?: BreadcrumbItem[];
  primaryAction?: ActionItem;
  secondaryActions?: ActionItem[];
  className?: string;
}

export function PageHeader({
  title,
  description,
  breadcrumbs,
  primaryAction,
  secondaryActions,
  className,
}: PageHeaderProps) {
  return (
    <div className={cn('flex flex-col gap-4 pb-6', className)}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav
          aria-label='breadcrumb'
          className='flex items-center text-sm text-muted-foreground'
        >
          {breadcrumbs.map((item, index) => (
            <React.Fragment key={index}>
              {index > 0 && <ChevronRight className='mx-2 h-4 w-4' />}
              {item.href ? (
                <a
                  href={item.href}
                  className='hover:text-foreground transition-colors'
                >
                  {item.label}
                </a>
              ) : (
                <span className='text-foreground font-medium'>
                  {item.label}
                </span>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}

      <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4'>
        <div className='space-y-1'>
          <h1 className='text-2xl font-bold tracking-tight'>{title}</h1>
          {description && (
            <p className='text-muted-foreground'>{description}</p>
          )}
        </div>

        {(primaryAction ||
          (secondaryActions && secondaryActions.length > 0)) && (
          <div className='flex items-center gap-2'>
            {secondaryActions?.map((action, index) => (
              <Button
                key={index}
                variant={action.variant || 'outline'}
                onClick={action.onClick}
                disabled={action.disabled}
                className='gap-2'
              >
                {action.icon}
                {action.label}
              </Button>
            ))}

            {primaryAction && (
              <Button
                variant={primaryAction.variant || 'default'}
                onClick={primaryAction.onClick}
                disabled={primaryAction.disabled}
                className='gap-2'
              >
                {primaryAction.icon}
                {primaryAction.label}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
